import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import classNames from 'classnames';

import { Close } from '@uikit/icons/Close';

import type { RewardPoolsReadableSearchParamsOrderBy } from '@shared/api/rewardPools';
import type { RewardPoolStatus } from '@shared/api/types/rewardPools';
import { REWARD_POOL_STATUS } from '@shared/api/types/rewardPools';
import { useAuth } from '@shared/common/providers/AuthProvider';

import * as Dialog from '@radix-ui/react-dialog';

import type {
  RewardPoolsCategoryFilterType} from './CategoryFilter';
import {
  categoryFilterOptionsInOrder,
  DEFAULT_CATEGORY_FILTER_VALUE,
  useRewardPoolsCategoryFilterQueryState,
} from './CategoryFilter';
import type {
  RewardPoolsPolicyFilterType} from './DistributionPolicyFilter';
import {
  DEFAULT_POOLS_POLICY_FILTER_VALUE,
  policyFiltersInOrder,
  useRewardPoolsDistributionPolicyFilterQueryState,
} from './DistributionPolicyFilter';
import {
  DEFAULT_SORT_BY_FILTER_VALUE,
  getSortByFiltersOptionsOrder,
  useRewardPoolsOrderByFilterQueryState,
} from './OrderByFilter';
import {
  DEFAULT_STATUS_FILTER_VALUE,
  statusFiltersInOrder,
  useRewardPoolsStatusFilterQueryState,
} from './StatusFilter';

export const FiltersDialog = () => {
  const { isAuth } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [status, setStatus] = useRewardPoolsStatusFilterQueryState();
  const [category, setCategory] = useRewardPoolsCategoryFilterQueryState();
  const [sortBy, setSortBy] = useRewardPoolsOrderByFilterQueryState();
  const [distributionPolicies, setDistributionPolicies] =
    useRewardPoolsDistributionPolicyFilterQueryState();

  const defaultValues = useMemo(() => {
    return {
      status,
      sortBy,
      category,
      distributionPolicy: distributionPolicies[0],
    };
  }, [status, sortBy, category, distributionPolicies]);

  const { register, handleSubmit, reset, watch, setValue } = useForm<{
    status: RewardPoolStatus;
    sortBy: RewardPoolsReadableSearchParamsOrderBy;
    category: RewardPoolsCategoryFilterType;
    distributionPolicy: RewardPoolsPolicyFilterType;
  }>({
    defaultValues,
  });
  const watchedStatus = watch('status');
  const watchedSortBy = watch('sortBy');

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset, isOpen]);

  const handleClearAllButtonClick = () => {
    setStatus(DEFAULT_STATUS_FILTER_VALUE);
    setSortBy(DEFAULT_SORT_BY_FILTER_VALUE);
    setCategory(DEFAULT_CATEGORY_FILTER_VALUE);
    setDistributionPolicies(DEFAULT_POOLS_POLICY_FILTER_VALUE);
  };

  const submit = handleSubmit((data) => {
    setStatus(data.status);
    setSortBy(data.sortBy);
    setCategory(data.category);
    const policyFilters = policyFiltersInOrder.find(
      (policy) => policy.id === data.distributionPolicy,
    )?.filters;

    setDistributionPolicies(policyFilters ?? DEFAULT_POOLS_POLICY_FILTER_VALUE);
    setIsOpen(false);
  });

  const sortOptions = useMemo(() => {
    return getSortByFiltersOptionsOrder(watchedStatus);
  }, [watchedStatus]);

  const activeSortOption = sortOptions.find((option) => option.value === watchedSortBy);
  const defaultSortOption = sortOptions[0];

  useEffect(() => {
    if (!activeSortOption?.value) {
      setValue('sortBy', defaultSortOption.value);
    }
  }, [activeSortOption?.value, defaultSortOption, setValue]);

  return (
    <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
      <Dialog.Trigger asChild>
        <button className="msq-btn msq-btn-lg msq-btn-secondary w-full md:hidden">Filters</button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay">
          <Dialog.Content
            aria-describedby={undefined}
            className="dialog dialog-mobile-menu dialog-sm"
          >
            <div className="dialog-header">
              <Dialog.Title className="dialog-title">Filters</Dialog.Title>

              <Dialog.Close asChild>
                <button className="dialog-close">
                  <Close className="dialog-close-icon" />
                </button>
              </Dialog.Close>
            </div>

            <form
              onChange={(t) => console.log(t)}
              onSubmit={submit}
              id="reward-pools-filters"
              className="dialog-body"
            >
              <h3 className="sub-menu-title sticky">Sort By</h3>

              {sortOptions.map(({ value, label }) => {
                return (
                  <label key={value} className="sub-menu-link">
                    <input {...register('sortBy')} className="radio" type="radio" value={value} />
                    {label}
                  </label>
                );
              })}

              <hr className="divider m-1" />

              <h3 className="sub-menu-title">Status</h3>
              {statusFiltersInOrder.map(({ id, label }) => {
                const isDisabled = id === REWARD_POOL_STATUS.CLAIMED && !isAuth;

                return (
                  <label
                    key={id}
                    className={classNames('sub-menu-link', {
                      'sub-menu-link-disabled': isDisabled,
                    })}
                  >
                    <input
                      {...register('status')}
                      disabled={isDisabled}
                      className="radio"
                      type="radio"
                      value={id}
                    />
                    {label}
                  </label>
                );
              })}

              <hr className="divider m-1" />
              <h3 className="sub-menu-title">Pool Type</h3>
              {policyFiltersInOrder.map(({ id, label }) => {
                return (
                  <label key={id} className={classNames('sub-menu-link')}>
                    <input
                      {...register('distributionPolicy')}
                      className="radio"
                      type="radio"
                      value={id}
                    />
                    {label}
                  </label>
                );
              })}
              <hr className="divider m-1" />

              <h3 className="sub-menu-title">Pool</h3>
              {categoryFilterOptionsInOrder.map(({ id, label }) => {
                return (
                  <label key={id} className="sub-menu-link">
                    <input {...register('category')} className="radio" type="radio" value={id} />
                    {label}
                  </label>
                );
              })}
            </form>

            <div className="dialog-footer dialog-menu-buttons">
              <button
                onClick={handleClearAllButtonClick}
                className="msq-btn msq-btn-lg msq-btn-secondary"
              >
                Clear All
              </button>
              <button
                type="submit"
                form="reward-pools-filters"
                className="msq-btn msq-btn-lg msq-btn-primary"
              >
                Apply
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
